/**
 * "Get the app" glue for the browser (logic: packages/core logic/getApp.ts). Reads the user agent and display mode,
 * fetches bundle.direct's release manifest once per page load, keeps the 30-day snooze in localStorage, and keeps
 * Safari's Smart App Banner meta tag (`apple-itunes-app`, app-argument = this page) in step with the URL once the
 * App Store listing is live. The web Worker also writes that tag into the HTML, since Safari reads it at load.
 */
import { GetApp, type AppAvailability, type AppPlatform } from "@bundle/core";
import { useSyncExternalStore } from "react";
import { isDesktop } from "./desktop.ts";
import { createStorage } from "./storage.ts";

const SNOOZE_KEY = "getApp.snoozedUntil";
const storage = createStorage();

interface State {
  availability: AppAvailability;
  snoozedUntil: number | null;
}

let state: State = { availability: GetApp.NO_AVAILABILITY, snoozedUntil: GetApp.parseSnooze(storage.get(SNOOZE_KEY)) };
let loading = false;
const listeners = new Set<() => void>();

function set(next: Partial<State>): void {
  state = { ...state, ...next };
  for (const l of listeners) l();
}

/** The browser, as `GetApp.detectPlatform` reads it. */
export function currentPlatform(): AppPlatform {
  if (typeof navigator === "undefined") return "other";
  const data = (navigator as Navigator & { userAgentData?: { platform?: string; mobile?: boolean } }).userAgentData;
  return GetApp.detectPlatform({ userAgent: navigator.userAgent, uaPlatform: data?.platform, uaMobile: data?.mobile, maxTouchPoints: navigator.maxTouchPoints ?? 0 });
}

/** Running as an installed PWA. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
  return ["standalone", "minimal-ui", "fullscreen", "window-controls-overlay"].some((mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches === true);
}

/** Reads the manifest once (no retries: on failure desktop downloads still work through the site's redirects). */
export function loadAvailability(fetcher: typeof fetch = fetch): void {
  if (loading || isDesktop()) return;
  loading = true;
  void fetcher(GetApp.MANIFEST_URL, { cache: "no-cache", credentials: "omit" })
    .then((res) => (res.ok ? res.json() : null))
    .then((json: unknown) => {
      if (json !== null) set({ availability: GetApp.availabilityFromManifest(json) });
      syncSmartBanner();
    })
    .catch(() => undefined);
}

/** × or Download: hide the prompt on this device for 30 days (for this page load if storage is unavailable). */
export function snoozeGetApp(now: number = Date.now()): void {
  const until = GetApp.snoozeUntil(now);
  storage.set(SNOOZE_KEY, String(until));
  set({ snoozedUntil: until });
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useGetAppState(): State {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

/** Opens a download (or store) link in a new tab. */
export function openDownload(url: string): void {
  window.open(url, "_blank", "noopener");
}

// ---- Safari's Smart App Banner ----

const META = "apple-itunes-app";

/** Adds, updates or removes the `apple-itunes-app` meta for the current URL. */
export function syncSmartBanner(): void {
  if (typeof document === "undefined" || isDesktop()) return;
  const path = `${location.pathname}${location.search}`;
  const content = GetApp.isPromptExcludedPath(path) ? null : GetApp.smartBannerContent(state.availability, location.href);
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[name="${META}"]`);
  if (content === null) {
    meta?.remove();
    return;
  }
  if (meta === null) {
    meta = document.createElement("meta");
    meta.name = META;
    document.head.appendChild(meta);
  }
  if (meta.content !== content) meta.content = content;
}

/** Test hook. */
export function resetGetAppForTests(next?: Partial<State>): void {
  loading = false;
  state = { availability: GetApp.NO_AVAILABILITY, snoozedUntil: null, ...next };
}
